import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useStoreState, useStoreActions } from 'easy-peasy';
import Slider from '@material-ui/core/Slider';

import { nf, trackEvent } from '~/utils';
import { media } from '~/styles/Utils';

import NumericInput from './NumericInput';
import ResultSentence from './ResultSentence';

import Button from '~/components/Button';
import InfoIcon from '~/components/Header/Assets/info.svg';
import PlayIcon from './play.svg';
import StopIcon from './stop.svg';

const ButtonWrapper = styled.div`
  display: flex;

  button {
    margin-left: auto;
    width: 150px;
  }
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  border-top: ${(props) =>
    props.borderTop ? `1px solid ${props.theme.colors.lightblue}` : 'none'};
  margin-bottom: 10px;
  line-height: 1.2;

  ${media.m`
    margin-bottom: 16px;
  `}
`;

const ListItemSlider = styled.div`
  margin-bottom: 0;

  ${media.m`
    margin-bottom: 16px;
  `}

  .MuiSlider-root {
    color: ${(props) => props.theme.colors.interaction};
  }

  .MuiSlider-markLabel {
    top: 24px;
    font-family: ${(props) => props.theme.fonts.sans};
    font-size: 12px;
  }

  .MuiSlider-markLabel[data-index='0'] {
    transform: none;
  }

  .MuiSlider-markLabel[data-index='5'] {
    transform: translate(-100%, 0);
  }

  .MuiSlider-thumb.Mui-focusVisible {
    box-shadow: none;
  }

  .MuiSlider-thumb:hover,
  .MuiSlider-thumb.MuiSlider-active {
    box-shadow: 0px 0px 0px 7px rgb(120 120 120 / 16%);
  }
`;

const Label = styled.div`
  width: 58%;
`;

const InputWrapper = styled.div`
  width: 40%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const InfoIconStyled = styled(InfoIcon)`
  width: 15px;
  height: 15px;
  margin-left: 5px;
`;

const InfoBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  pointer-events: none;
  user-select: none;
  font-size: 13px;
  background: white;
  padding: 10px;
  border: 1px solid #eee;
  z-index: 100;
  border-radius: 4px;
  box-shadow: 0 2px 6px 2px rgba(0, 0, 0, 0.08);
`;

const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const PlayButton = styled.div`
  width: 36px;
  height: 36px;
  margin-right: 15px;
  background: ${(p) => p.theme.colors.interaction};
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  line-height: 1;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;

  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

const marks = config.years.map((value) => ({
  value,
  label: value,
}));

const getNextYear = (year) => {
  const currYearIndex = config.years.indexOf(year);
  const nextYearIndex =
    currYearIndex === config.years.length - 1 ? 0 : currYearIndex + 1;

  return config.years[nextYearIndex];
};

const IncomeCalculator = () => {
  const playTimeout = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [income, setIncome] = useState(64500);
  const [membersAbove14, setMembersAbove14] = useState(2);
  const [membersBelow14, setMembersBelow14] = useState(2);
  const [year, setYear] = useState(2019);
  const [percentile, setPercentile] = useState(null);

  const storeYear = useStoreState((state) => state.app.year);
  const isRelView = useStoreState((state) => state.app.isRelView);
  const data = useStoreState((state) => state.app.data);
  const storeIncome = useStoreState((state) => state.income.income);
  const storeMembersAbove14 = useStoreState(
    (state) => state.income.membersAbove14
  );
  const storeMembersBelow14 = useStoreState(
    (state) => state.income.membersBelow14
  );

  const changeYear = useStoreActions((state) => state.app.changeYear);
  const {
    setIncome: setIncomeAction,
    setMembersAbove14: setMembersAbove14Action,
    setMembersBelow14: setMembersBelow14Action,
  } = useStoreActions((actions) => actions.income);

  const [tooltip, setTooltip] = useState({
    visible: false,
    position: { top: 0, left: 0 },
  });

  const infoButtonRef = useRef();
  const containerRef = useRef();

  const handleInfoIcon = () => {
    const infoBtnPosition = infoButtonRef.current.getBoundingClientRect();
    const containerPosition = containerRef.current.getBoundingClientRect();

    const position = {
      top: infoBtnPosition.top - containerPosition.top + 20,
      left: Math.max(infoBtnPosition.left - containerPosition.left - 100, 0),
    };

    setTooltip({
      visible: true,
      position,
    });
  };

  const handleInfoIconOut = () => {
    setTooltip((tData) => ({
      ...tData,
      visible: false,
    }));
  };

  const tooltipStyle = {
    top: tooltip.position.top,
    left: tooltip.position.left,
  };

  const onIncomeChange = (nextIncome) => {
    setIncome(nextIncome);
    trackEvent('set_income', nextIncome);
  };

  const setAbove14 = (above14) => {
    setMembersAbove14(above14);
    trackEvent('set_above14', above14);
  };

  const setBelow14 = (below14) => {
    setMembersBelow14(below14);
    trackEvent('set_below14', below14);
  };

  const parser = (value) => {
    const result = +value.replace('.', '');
    if (Number.isNaN(result)) {
      return 0;
    }
    return Math.max(0, Math.min(result, 999999));
  };

  useEffect(() => {
    if (data && !percentile) {
      const weightedIncome = Math.round(
        income / (1 + (membersAbove14 - 1) * 0.5 + membersBelow14 * 0.3)
      );
      const perc = data
        .find((d) => d.year === year)
        ?.data?.find((d) => weightedIncome <= d[config.columns.AVG]);

      setPercentile(perc);
    }
  }, [income, membersAbove14, membersBelow14, data]);

  useEffect(() => {
    if (isPlaying) {
      playTimeout.current = window.setTimeout(() => {
        const nextYear = getNextYear(storeYear);
        changeYear(nextYear);
      }, 1000);
    } else {
      window.clearTimeout(playTimeout.current);
    }
  }, [isPlaying, storeYear]);

  const togglePlay = () => {
    if (isPlaying) {
      window.clearTimeout(playTimeout.current);
    } else {
      const nextYear = getNextYear(storeYear);
      changeYear(nextYear);
    }

    setIsPlaying((ip) => !ip);
  };

  const setPosition = () => {
    setIncomeAction(income);
    setMembersAbove14Action(membersAbove14);
    setMembersBelow14Action(membersBelow14);
    setYear(storeYear);

    if (data) {
      const weightedIncome = Math.round(
        income / (1 + (membersAbove14 - 1) * 0.5 + membersBelow14 * 0.3)
      );
      const perc = data
        .find((d) => d.year === storeYear)
        ?.data?.find((d) => weightedIncome <= d[config.columns.AVG]);

      setPercentile(perc);
    }
  };

  const buttonActive =
    year !== storeYear ||
    income !== storeIncome ||
    membersAbove14 !== storeMembersAbove14 ||
    membersBelow14 !== storeMembersBelow14;

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      {tooltip.visible && (
        <InfoBox style={tooltipStyle}>
          Einkommen umfassen alle Einnahmen der Haushaltsmitglieder, zum
          Beispiel Löhne, Zinsen, Mieteinnahmen, Unterhaltszahlungen, Renten
          (nach Abzug von Steuern und inklusive Transfers wie Kindergeld). Im
          Fall einer selbst bewohnten Immobilie werden auch die gesparten
          Mietausgaben addiert (abzüglich der Kosten für Hypotheken und
          Instandhaltung).
        </InfoBox>
      )}
      <ListItem>
        <Label>
          Wie hoch ist Ihr jährliches Haushaltsbruttoeinkommen in Euro?
          <span ref={infoButtonRef}>
            <InfoIconStyled
              onMouseEnter={handleInfoIcon}
              onMouseLeave={handleInfoIconOut}
            />
          </span>
        </Label>
        <InputWrapper>
          <NumericInput
            formatter={nf}
            parser={parser}
            min={0}
            step={1000}
            max={999999}
            onChange={onIncomeChange}
            value={income}
          />
        </InputWrapper>
      </ListItem>
      <ListItem>
        <Label>
          Wie viele Personen <strong>ab</strong> 14 Jahren leben in Ihrem
          Haushalt?
        </Label>
        <InputWrapper>
          <NumericInput
            min={1}
            max={10}
            onChange={setAbove14}
            value={membersAbove14}
          />
        </InputWrapper>
      </ListItem>
      <ListItem>
        <Label>
          Wie viele Personen <strong>unter</strong> 14 Jahren leben in Ihrem
          Haushalt?
        </Label>
        <InputWrapper>
          <NumericInput
            min={0}
            max={10}
            onChange={setBelow14}
            value={membersBelow14}
          />
        </InputWrapper>
      </ListItem>
      <ButtonWrapper>
        <Button isActive={buttonActive} onClick={setPosition}>
          Position berechnen
        </Button>
      </ButtonWrapper>

      {percentile && (
        <ListItem>
          <ResultSentence
            isRel={isRelView}
            percentile={percentile}
            year={year}
            buttonActive={buttonActive}
          />
        </ListItem>
      )}

      <ListItemSlider>
        <div>Welches Jahr wollen Sie betrachten?</div>
        <SliderWrapper>
          <PlayButton onClick={togglePlay}>
            {isPlaying ? <StopIcon /> : <PlayIcon />}
          </PlayButton>
          <Slider
            value={storeYear}
            aria-labelledby="discrete-slider-restrict"
            step={null}
            valueLabelDisplay="off"
            marks={marks}
            min={1998}
            max={2019}
            track={false}
            onChange={(_, value) => console.log(value) || changeYear(value)}
          />
        </SliderWrapper>
      </ListItemSlider>
    </div>
  );
};

export default IncomeCalculator;
